export const HISTORY_COLLECTION = {
  risks: "risks",
  issues: "issues",
  assumptions: "assumptions",
  decisions: "decisions",
  "change-requests": "change_requests",
  lessons: "lessons",
  benefits: "benefits",
  milestones: "milestones",
  dependencies: "dependencies",
  "status-reports": "status_updates",
};

// Field specs for editing records outside their own project page (the cross-project registers).
// Kinds match CONTROL_VIEWS on the backend; updatePath is the PUT route for one record.
export const RECORD_SPECS = {
  risks: {
    label: "Risk",
    updatePath: "/risks",
    fields: [
      { key: "title", label: "Title", required: true, span: 2 },
      { key: "risk_type", label: "Risk Type" },
      { key: "owner", label: "Owner" },
      { key: "probability", label: "Probability", type: "select", options: ["Very Low", "Low", "Medium", "High", "Very High"] },
      { key: "impact", label: "Impact", type: "select", options: ["Very Low", "Low", "Medium", "High", "Very High"] },
      { key: "probability_score", label: "Probability Score (1-5)", type: "number" },
      { key: "impact_score", label: "Impact Score (1-5)", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Open", "Mitigated", "Closed"] },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "escalation_level", label: "Escalation", type: "select", options: ["None", "Level 1", "Level 2", "Level 3"] },
      { key: "cost_of_mitigation", label: "Cost of Mitigation", type: "number" },
      { key: "include_in_status_report", label: "Include on status report", type: "checkbox" },
      { key: "description", label: "Description", type: "textarea", span: 2 },
      { key: "mitigation", label: "Mitigation", type: "textarea", span: 2 },
    ],
  },
  issues: {
    label: "Issue",
    updatePath: "/issues",
    fields: [
      { key: "title", label: "Title", required: true, span: 2 },
      { key: "issue_type", label: "Issue Type" },
      { key: "owner", label: "Owner" },
      { key: "severity", label: "Severity", type: "select", options: ["Low", "Medium", "High"] },
      { key: "priority", label: "Priority", type: "select", options: ["Critical", "High", "Medium", "Low"] },
      { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Resolved", "Closed"] },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "escalation_level", label: "Escalation", type: "select", options: ["None", "Level 1", "Level 2", "Level 3"] },
      { key: "include_in_status_report", label: "Include on status report", type: "checkbox" },
      { key: "description", label: "Description", type: "textarea", span: 2 },
      { key: "resolution", label: "Resolution Plan", type: "textarea", span: 2 },
    ],
  },
  assumptions: {
    label: "Assumption",
    updatePath: "/assumptions",
    fields: [
      { key: "title", label: "Title", required: true, span: 2 },
      { key: "category", label: "Category" },
      { key: "responsibility", label: "Responsibility" },
      { key: "owner", label: "Owner" },
      { key: "confidence", label: "Confidence", type: "select", options: ["Low", "Medium", "High"] },
      { key: "status", label: "Status", type: "select", options: ["Open", "Validated", "Invalidated", "Closed"] },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "actions", label: "Actions", type: "textarea", span: 2 },
      { key: "description", label: "Description", type: "textarea", span: 2 },
    ],
  },
  decisions: {
    label: "Decision",
    updatePath: "/decisions",
    fields: [
      { key: "title", label: "Decision", required: true, span: 2 },
      { key: "criticality", label: "Criticality", type: "select", options: ["Low", "Medium", "High", "Critical"] },
      { key: "owner", label: "Owner" },
      { key: "approver", label: "Approver" },
      { key: "approval_status", label: "Approval Status", type: "select", options: ["Pending", "Approved", "Rejected"] },
      { key: "decision_date", label: "Decision Date", type: "date" },
      { key: "description", label: "Description", type: "textarea", span: 2 },
      { key: "rationale", label: "Rationale", type: "textarea", span: 2 },
    ],
  },
  "change-requests": {
    label: "Change Request",
    updatePath: "/change-requests",
    fields: [
      { key: "title", label: "Change Title", required: true, span: 2 },
      { key: "reason_for_change", label: "Reason for Change", span: 2 },
      { key: "cost_impact", label: "Cost Impact", type: "number" },
      { key: "schedule_impact_days", label: "Schedule Impact (days)", type: "number" },
      { key: "approval_level", label: "Approval Level", type: "select", options: ["Level 1", "Level 2", "Level 3"] },
      { key: "approver", label: "Approver" },
      { key: "approval_status", label: "Approval Status", type: "select", options: ["Pending", "Approved", "Rejected"] },
      { key: "requested_by", label: "Requested By" },
      { key: "decision_date", label: "Decision Date", type: "date" },
      { key: "description", label: "Description", type: "textarea", span: 2 },
    ],
  },
  lessons: {
    label: "Lesson",
    updatePath: "/lessons",
    fields: [
      { key: "title", label: "Lesson", required: true, span: 2 },
      { key: "stage", label: "Project Stage" },
      { key: "topic", label: "Topic" },
      { key: "category", label: "Category", type: "select", options: ["Success", "Improvable"] },
      { key: "owner", label: "Owner" },
      { key: "description", label: "What happened", type: "textarea", span: 2 },
      { key: "recommendation", label: "Recommendation", type: "textarea", span: 2 },
    ],
  },
  benefits: {
    label: "Benefit",
    updatePath: "/benefits",
    fields: [
      { key: "title", label: "Benefit", required: true, span: 2 },
      { key: "category", label: "Category", type: "select", options: ["Financial", "Efficiency", "Customer", "Compliance", "Strategic"] },
      { key: "rating", label: "Rating", type: "select", options: ["Low", "Medium", "High"] },
      { key: "target_value", label: "Target Value" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status", type: "select", options: ["Planned", "In Progress", "Realized", "Not Realized"] },
      { key: "closed_date", label: "Closed Date", type: "date" },
      { key: "description", label: "Description", type: "textarea", span: 2 },
    ],
  },
  milestones: {
    label: "Milestone",
    updatePath: "/milestones",
    fields: [
      { key: "title", label: "Milestone", required: true, span: 2 },
      { key: "milestone_type", label: "Type", type: "select", options: ["Major", "Minor", "Stage Gate", "Key Deliverable"] },
      { key: "stage", label: "Project Stage" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status", type: "select", options: ["Not Started", "In Progress", "Complete", "Delayed"] },
      { key: "baseline_date", label: "Baseline Date", type: "date" },
      { key: "forecast_date", label: "Forecast Date", type: "date" },
      { key: "actual_date", label: "Actual Date", type: "date" },
      { key: "include_in_status_report", label: "Include on status report", type: "checkbox" },
      { key: "description", label: "Description", type: "textarea", span: 2 },
    ],
  },
  dependencies: {
    label: "Dependency",
    updatePath: "/dependencies",
    note: "Direction and counterparty are fixed once raised, so the other project's acceptance still means something.",
    fields: [
      { key: "title", label: "Title", required: true, span: 2 },
      { key: "owner", label: "Our Owner" },
      { key: "counterparty_owner", label: "Counterparty Owner" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "rag", label: "RAG", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "escalation_level", label: "Escalation", type: "select", options: ["None", "Level 1", "Level 2", "Level 3"] },
      { key: "description", label: "Description", type: "textarea", span: 2 },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  "status-reports": {
    label: "Status Report",
    updatePath: "/status-updates",
    note: "Approved reports are locked. Reopen the report on the project to change it.",
    fields: [
      { key: "percent_complete", label: "% Complete", type: "number" },
      { key: "overall_rag", label: "Overall RAG", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "plan_rag", label: "Plan", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "resource_rag", label: "Resource", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "finance_rag", label: "Finance", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "benefits_rag", label: "Benefits", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "vendor_rag", label: "Vendor", type: "select", options: ["Green", "Amber", "Red"] },
      { key: "exec_summary", label: "Exec Summary", type: "textarea", span: 2 },
      { key: "progress_this_period", label: "Progress This Period", type: "textarea", span: 2 },
      { key: "upcoming_focus", label: "Upcoming Focus", type: "textarea", span: 2 },
    ],
  },
};
